import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { optimizeImage } from '../utils/imageOptimizer';
import './ContactPage.css';
import ScrollIndicator from './ScrollIndicator';

const ContactPage = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://formsubmit.co/ajax/Info@caninecrown.in", {
        method: "POST",
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          _subject: "New Contact Form Submission - Canine Crown",
          _template: "table"
        })
      });

      const result = await response.json();
      
      if (response.ok && (result.success === "true" || result.success === true)) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error(result.message || "Failed to submit form.");
      }
    } catch (err) {
      console.error(err);
      setStatus({ 
        submitting: false, 
        success: false, 
        error: err.message || "Something went wrong. Please try again." 
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.hash === '#contact-form') {
      setTimeout(() => {
        const element = document.getElementById('contact-form');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 600); // Wait for page transition and ScrollToTop to finish
    }
  }, [location]);

  const heroBg = isMobile 
    ? 'https://caninecrown.ascentbw.com/Image/Contact/ContactHeroMob.webp'
    : 'https://caninecrown.ascentbw.com/Image/Contact/Hero.webp';

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div 
          className="contact-hero-bg hero-animate-down" 
          style={{ backgroundImage: `url(${optimizeImage(heroBg, { width: isMobile ? 768 : 1920 })})` }}
        ></div>
        <div className="contact-hero-content container hero-animate-up">
          <h1>We're Here For<br />Your Pet's Needs</h1>
        </div>
        <ScrollIndicator />
      </section>

      {/* Main Content Section */}
      <section className="contact-main container">
        <div className="contact-wrapper">
          {/* Left Form */}
          <div className="contact-form-section" id="contact-form">
            <h2>Send Us A Message</h2>
            {status.success && (
              <div className="contact-status success-message animate-fade-in">
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                <div>
                  <h4>Thank You!</h4>
                  <p>Your message has been sent successfully. We'll get back to you shortly.</p>
                </div>
              </div>
            )}
            {status.error && (
              <div className="contact-status error-message animate-fade-in">
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <div>
                  <h4>Submission Failed</h4>
                  <p>{status.error}</p>
                </div>
              </div>
            )}
            <form
              className="contact-form"
              onSubmit={handleSubmit}
            >
              <input 
                type="text" 
                name="name" 
                placeholder="Your Name" 
                value={formData.name}
                onChange={handleChange}
                required 
              />
              <input 
                type="email" 
                name="email" 
                placeholder="Your E-Mail" 
                value={formData.email}
                onChange={handleChange}
                required 
              />
              <input 
                type="tel" 
                name="phone" 
                placeholder="Your Number" 
                value={formData.phone}
                onChange={handleChange}
                required 
              />
              <textarea 
                name="message" 
                placeholder="Your Message" 
                rows="5" 
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={status.submitting}
              >
                {status.submitting ? 'Sending...' : 'Send Enquiry'}
              </button>
            </form>
          </div>

          {/* Right Info Container */}
          <div className="contact-info-section">
            <div className="contact-info-header">
              <h2>Let's Talk About Your Pet</h2>
              <p>Have a product enquiry, distribution enquiry, retail partnership request, or general question? Our team will be happy to assist you.</p>
            </div>
            <div className="contact-info-image">
              <img 
                src={optimizeImage("https://caninecrown.ascentbw.com/Image/Contact/ConForm.webp", { width: 600, height: 600 })} 
                alt="Dog running" 
                loading="lazy" 
                width="600" 
                height="600" 
              />
              <div className="image-overlay-info">
                <div className="info-row">
                  <div className="info-icon">
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <div className="info-text">
                    <span className="label">E-Mail Us At</span>
                    <span className="value">info@caninecrown.in</span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-icon">
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"></path></svg>
                  </div>
                  <div className="info-text">
                    <span className="label">Call Us At</span>
                    <span className="value">+91 74488 80880</span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-icon">
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  </div>
                  <div className="info-text">
                    <span className="label">Vanagaram, Chennai - 600095</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact-map container">
        <iframe 
          src="https://maps.google.com/maps?q=Canine%20Crown%20India%20Pvt%20Ltd%2C%20Ground%20Floor%20%26%201st%20Floor%2C%20No.%20135%2F2%2C%20Balamurugan%20Kovil%20Street%2C%20Sunaresan%20Nagar%2C%20Anna%20Industrial%20Estate%2C%20Vanagaram%2C%20Chennai%2C%20Tamil%20Nadu%20600095&t=&z=17&ie=UTF8&iwloc=&output=embed" 
          width="100%" 
          height="450" 
          style={{ border: 0 }} 
          allowFullScreen 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Canine Crown India Pvt Ltd Location Map"
        ></iframe>
      </section>
      {/* Contact Cards */}
      <section className="contact-cards container">
        <div className="contact-card">
          <div className="card-icon red-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
          </div>
          <h3>Address</h3>
          <h4>Canine Crown India Pvt Ltd</h4>
          <p>Ground Floor & 1st Floor, No. 135/2,<br />Balamurugan Kovil Street, Sunaresan Nagar,<br />Anna Industrial Estate, Vanagaram,<br />Chennai, Tamil Nadu 600095.</p>
        </div>

        <div className="contact-card">
          <div className="card-icon red-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
          </div>
          <h3>Email</h3>
          <h4>info@caninecrown.in</h4>
        </div>

        <div className="contact-card">
          <div className="card-icon red-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
          </div>
          <h3>Call Us</h3>
          <h4>+91 7448 880 880</h4>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
